/**************************************************************************************************/
/** @import Node Packages                                                                        **/
/**************************************************************************************************/
import { AxiosResponse } from 'axios'
import { defineComponent } from 'vue'

/**************************************************************************************************/
/** @import Classes/Records                                                                      **/
/**************************************************************************************************/
import { ActionItem } from '@/Classes/Records/ActionItem'
import { RecordHelper } from '@/Classes/Records/RecordHelper'

/**************************************************************************************************/
/** @import Constants                                                                            **/
/**************************************************************************************************/
import { AppValues } from '@/Constants/AppValues'
import { Breakpoints } from '@/Constants/Breakpoints'
import { Component } from '@/Constants/Component'

/**************************************************************************************************/
/** @import Helpers/Components                                                                   **/
/**************************************************************************************************/
import { BMInterfaces } from '@/Helpers/Components/DataTable/BMInterfaces'

/**************************************************************************************************/
/** @import Helpers/Documents                                                                    **/
/**************************************************************************************************/
import { ExcelBook } from '@/Helpers/Documents/Excel/ExcelBook'
import { ExcelSheet } from '@/Helpers/Documents/Excel/ExcelSheet'

/**************************************************************************************************/
/** @import Helpers/HTTP                                                                         **/
/**************************************************************************************************/
import { AxiosHelper } from '@/Helpers/HTTP/AxiosHelper'

/**************************************************************************************************/
/** @import Helpers/Utils                                                                        **/
/**************************************************************************************************/
import { AlgorithmsHelper } from '@/Helpers/Utils/AlgorithmsHelper'
import { PrintHelper } from '@/Helpers/Utils/PrintHelper'
import { TypesHelper } from '@/Helpers/Utils/TypesHelper'

/**************************************************************************************************/
/** @import Mixins                                                                               **/
/**************************************************************************************************/
import MixinBase from '@/Mixins/MixinBase'
import MixinComponent from '@/Mixins/MixinComponent'
import MixinResponsive from '@/Mixins/MixinResponsive'

/**************************************************************************************************/
/** @internal Const @name DataTableConstants                                                     **/
/**************************************************************************************************/
const DataTableConstants = Component.VueComponents.Global.DataTable

/**************************************************************************************************/
/** @internal Const @name DataTable                                                              **/
/**************************************************************************************************/
const DataTable = defineComponent(
{
	/*************************************************/
	/** @Properties                                 **/
	/*************************************************/
	name: 'DataTable', mixins: [MixinBase, MixinComponent, MixinResponsive],

	props:
	{
		bordered: { type: Boolean, default: false },
		condensed: Boolean,
		fontsize: String,
		fixed: { type: Boolean, default: false },
		forceNewButtonEnabled: { type: Boolean, default: false },
		itemsPerPage: { type: Number, default: DataTableConstants.DefaultValues.ItemsPerPage },
		newButtonText: { type: String, default: 'Nuevo Registro' },
		newButtonVariant: { type: String, default: 'green' },
		show: Boolean,
		showNewButton: { type: Boolean, default: true },
	},

	/*************************************************/
	/** @Data                                       **/
	/*************************************************/
	data()
	{
		return {
			// Propiedades Principales del Componente.
			actions: [] as Array<any>,
			currentRow: undefined as any,
			currentPage: 1,
			items: [] as Array<any>,
			itemsProvider: undefined as Function | undefined,
			fields: [] as Array<any>,
			fieldsRemoved: [] as Array<any>,
			search: { input: '', result: undefined as any },
			selectable: false,
			sortBy: '',
			sortDesc: false,
			sortDirection: 'asc',
			totalRows: 1,

			// Constantes
			constants:
			{
				FetchingItemsFromServer: 'Cargando registros desde el servidor...',
				NoItemsMatchSearchCriteria: `Ningun resultado coincide con el criterio de busqueda '%s'`,
				NoItemsToDisplay: 'No se encuentran registros para mostrar',
				RefreshingItems: 'Actualizando Registros...'
			},

			// Permisos Activos.
			permissions:
			{
				NEW_BUTTON: null as any
			},

			// Propiedad de Estados.
			states:
			{
				axiosHelperForInputSearchRequest: new AxiosHelper(),
				emptyText: undefined as string | undefined,
				exportDataAsAsync: false,
				exportDataFilterType: AppValues.DataTableExportDataFilterType.PERIOD,
				exportFieldNamesToExclude: ['actions'],
				exportFileFormatSelected: undefined as any,
				filterBy: '',
				isFetching: false,
				isLocalSearch: true,
				isSearchCurrentlyActive: false,
				isUsingCustomProvider: false,
				maxHeight: undefined as number | string | undefined,
				newButtonDisabled: false,
				paginationNumButtons: DataTableConstants.DefaultValues.NumPaginationButtons,
				preventDefaultOnInputSearchClear: false,
				preventDefaultOnPaginationChange: false,
				preventDefaultStacked: false,
				rowEventDisabled: undefined as any,
				searchBy: '',
				searchInputDisabled: true,
				showControls: true,
				showExportButtons: true,
				showExportAsCSVButton: true,
				showExportAsJSONButton: true,
				showExportAsXLSXButton: true,
				showExportDataSelection: false,
				showPagination: true,
				showRefreshButton: true,
				showSearchBar: true,
				stacked: false,
				totalPages: 1,
				stackedDetailsItemFlags: [] as Array<Component.VueComponents.Global.DataTable.ParsedFlagsObject>,
				noDataMessage: 'No hay datos disponibles',
				xlsxWorkSheetName: 'Sheet 1'
			}
		}
	},

	/*************************************************/
	/** @Hooks                                      **/
	/*************************************************/
	created() : void
	{
		this.ResetEmptyText()
	},

	mounted() : void
	{
		this.UpdateNumRows(this.items.length)
	},

	/*************************************************/
	/** @Computed                                   **/
	/*************************************************/
	computed:
	{
		GetExportDataChoices() : Array<{ text: string, value: number }>
		{
			switch (this.states.exportDataFilterType)
			{
				case AppValues.DataTableExportDataFilterType.PERIOD:
					return [
						{ text: AppValues.PeriodsChoiceFriendlyTextList.CURRENT_MONTH, value: AppValues.PeriodsChoiceList.CURRENT_MONTH },
						{ text: AppValues.PeriodsChoiceFriendlyTextList.LAST_15_DAYS, value: AppValues.PeriodsChoiceList.LAST_15_DAYS },
						{ text: AppValues.PeriodsChoiceFriendlyTextList.LAST_30_DAYS, value: AppValues.PeriodsChoiceList.LAST_30_DAYS },
						{ text: AppValues.PeriodsChoiceFriendlyTextList.LAST_12_MONTHS, value: AppValues.PeriodsChoiceList.LAST_12_MONTHS },
						{ text: AppValues.PeriodsChoiceFriendlyTextList.CURRENT_YEAR, value: AppValues.PeriodsChoiceList.CURRENT_YEAR }
					]
				case AppValues.DataTableExportDataFilterType.QUANTITY:
					return [
						{ text: AppValues.QuantityChoiceFriendlyTextList.LAST_10_ITEMS, value: AppValues.QuantityChoiceList.LAST_10_ITEMS },
						{ text: AppValues.QuantityChoiceFriendlyTextList.LAST_20_ITEMS, value: AppValues.QuantityChoiceList.LAST_20_ITEMS },
						{ text: AppValues.QuantityChoiceFriendlyTextList.LAST_30_ITEMS, value: AppValues.QuantityChoiceList.LAST_30_ITEMS },
						{ text: AppValues.QuantityChoiceFriendlyTextList.LAST_50_ITEMS, value: AppValues.QuantityChoiceList.LAST_50_ITEMS },
						{ text: AppValues.QuantityChoiceFriendlyTextList.ALL_ITEMS, value: AppValues.QuantityChoiceList.ALL_ITEMS }
					]
			}
		},

		GetExportDataChoicesTitle() : string
		{
			switch (this.states.exportDataFilterType)
			{
				case AppValues.DataTableExportDataFilterType.PERIOD:   return 'Periodo'
				case AppValues.DataTableExportDataFilterType.QUANTITY: return 'Cantidad'
			}
		},

		GetFieldsNames() : Array<any>
		{
			if (this.fields.length > 0) return this.fields.map((f: any) => f.key)
			return []
		},

		GetSelectedExportTypeLabel() : string
		{
			switch (this.states.exportFileFormatSelected)
			{
				case 'CSV' : return 'CSV (.csv)'
				case 'JSON': return 'JSON (.json)'
				case 'XLSX': return 'Excel (.xlsx)'
				default: return ''
			}
		},

		IsStacked() : boolean
		{
			return this.states.stacked as boolean
		},

		Pagination() : any
		{
			return this.$refs.pagination || { localNumberOfPages: 1 }
		},

		SortOptions() : Array<{ text: string, value: any }>
		{
			// Listado completo para el 'Select' de Filtro.
			const options: Array<{ text: string, value: any }> = []

			// Opciones provenientes de 'fields'.
			if (this.fields.length > 0) this.fields
				.filter((f: any) => f.sortable)
				.map((f: any) => options.push({ text: f.label, value: f.key })
			)

			// Opciones provenientes de 'stackedDetailsItemFlags'.
			if (this.IsStacked) this.states.stackedDetailsItemFlags.forEach((x) => {
				options.push({ text: x.text, value: x.flagKey })
			})

			return options?.sort((a, b) => a.text > b.text ? 1 : -1)
		}
	},

	/*************************************************/
	/** @Methods                                    **/
	/*************************************************/
	methods:
	{
		AddRow(item: any) : void
		{
			this.items.push(item)
			this.UpdateNumRows(this.items.length)
		},

		ApplyCustomCellClasses(field: any, value: any) : string
		{
			if (this.IsStacked) return ''
			let classes = Array.isArray(value) ? 'content-select' : field.entry?.editable ? 'content-editable' :'content '
			if (field?.align?.item) classes += `align-${ field?.align?.item } `
			return classes.trim()
		},

		ApplyCustomCellContentStyle(cell: any) : object | string
		{
			if (typeof cell !== 'object') return ''
			return cell.style
		},

		ApplyCustomHeadClasses(field: any) : string
		{
			let classes = ''
			if (field?.align?.field) classes += `text-${ field?.align?.field } `
			return classes.trim()
		},

		ApplyCustomHeadContentStyle(field: any) : string
		{
			let style = ''
			if (field?.align?.item) style += `text-${ field?.align?.item } `
			return style.trim()
		},

		BindFieldActionPermissions() : void
		{
			if (this.actions.length > 0 && this.items.length > 0)
			{
				for (const actionField of this.actions)
				{
					const item = this.items[0][actionField]
					for (const action of item) this.SetPermission(action.permission, false)
				}
				this.$emit('onDTFieldActionPermissionsBound')
			}
		},

		ClearAll() : void
		{
			this.ClearCurrentRow()
			this.ClearData()
			this.ClearSortBy()
			this.ClearPagination()
		},

		ClearCurrentRow() : void
		{
			this.currentRow = undefined
			const table: any = this.$refs?.table
			table?.clearSelected()
		},

		ClearData() : void
		{
			this.fields = []
			this.items = []
			this.itemsProvider = undefined
			this.Set<DataTableRef['states']>({ stackedDetailsItemFlags: [], isUsingCustomProvider: false })
			this.ClearCurrentRow()
		},

		ClearPagination() : void
		{
			this.UpdateTotalPages(1)
			this.currentPage = 1
		},

		ClearSortBy() : void
		{
			this.sortBy = ''
			this.states.filterBy = ''
			this.search.input = ''
			this.search.result = undefined
		},

		ColorKeyToColorVar(key: string) : string
		{
			if (key !== undefined)
			{
				switch (key)
				{
					case 'TealDarker': return 'teal-darker'
					case 'TealDark':   return 'teal-dark'
					default:           return key.toLowerCase()
				}
			}
			return ''
		},

		async DoAsyncFetch(p: Promise<any>) : Promise<any>
		{
			try
			{
				this.Set<DataTableRef['states']>({ isFetching: true })
				const response = await p
				if (response)
				{
					this.Set<DataTableRef['states']>({ isFetching: false })
					return response
				}
			}
			catch (err)
			{
				this.ResetFetchState()
				return null
			}
		},

		async DoInputSearch(endpoint: string | null, dataParser: any, params: any, responseHandler?: (response: AxiosResponse<any>) => { data: Array<any>, totalPages: number }) : Promise<void>
		{
			const { axiosHelperForInputSearchRequest, filterBy, isLocalSearch, preventDefaultOnInputSearchClear } = this.states
			const { input } = this.search

			// Emitir evento cuando se elimine el criterio de busqueda.
			if (!isLocalSearch && input === '') {
				this.Set<DataTableRef['states']>({ isSearchCurrentlyActive: false })
				this.search.result = undefined

				// Comprobación de 'Flag' temporal 'preventDefaultOnInputSearchClear'.
				if (preventDefaultOnInputSearchClear) {
					this.Set<DataTableRef['states']>({ preventDefaultOnInputSearchClear: false })
					return
				}

				this.$emit('onDTPaginationChanged', 1)
				this.SetPaginationPage(1)
				return
			}

			// Asegurar que existe un Input y un Filtro especificado.
			if (input !== '' && filterBy !== '')
			{
				let preventSearchOnInputKeyPress = true
				let items = [], totalPages: number | null = null
				const result = []

				// Busqueda de Registros Locales.
				if (isLocalSearch)
				{
					items = (await this.GetIterableItems()) as Array<any>
					preventSearchOnInputKeyPress = false
				}

				// Busqueda de Registros desde Endpoint.
				if (!isLocalSearch && (endpoint && dataParser && params !== undefined))
				{

					// Cancelar cualquier Petición activa.
					axiosHelperForInputSearchRequest?.abortRequest()

					// Almacenar referencia de 'AxiosHelper'.
					const axiosHelper = new AxiosHelper()
					this.Set<DataTableRef['states']>({ axiosHelperForInputSearchRequest: axiosHelper })

					// Realizar Petición a Servidor.
					try
					{
						const response = await axiosHelper.get(endpoint, { params })
						if (response)
						{
							// Si los 'registros' se encuentran en una propiedad diferente a la por defecto, obtenerlos a traves del 'responseHandler'.
							let responseBody = response.data.body

							if (responseHandler)
							{
								const customResponse = responseHandler(response)
								responseBody = customResponse.data
								totalPages   = customResponse.totalPages
							}

							const parsedData = dataParser(responseBody)
							items = parsedData?.items as Array<any>
							preventSearchOnInputKeyPress = false
						}
					}
					catch (err: any) { return PrintHelper.error(err.response) }
				}

				// Impedir continuar con Proceso de Datos (en caso particular).
				if (preventSearchOnInputKeyPress) return

				// Buscar coincidencias en los Registros.
				for (const item of items)
				{
					if (filterBy === 'actions')
					{
						if (item[filterBy].length > 0)
						{
							const inputText = input.toLowerCase()
							const elementText = item[filterBy][0].text.toString().toLowerCase()
							if (AlgorithmsHelper.KMPSearch(inputText, elementText) !== -1) result.push(item)
						}
					}
					else if (filterBy === 'rut')
					{
						const inputText = input.toLowerCase().replace(/\./g, '')
						const itemText = item[filterBy].toString().toLowerCase().replace(/\./g, '')
						if (AlgorithmsHelper.KMPSearch(inputText, itemText) !== -1) result.push(item)
					}
					else if (item[filterBy] !== null)
					{
						const inputText = input.toLowerCase()
						const itemText = item[filterBy].toString().toLowerCase()
						if (AlgorithmsHelper.KMPSearch(inputText, itemText) !== -1) result.push(item)
					}
				}

				// Registros obtenidos desde un 'Endpoint' externo.
				if (!isLocalSearch && (endpoint && dataParser && params !== undefined))
				{
					if (responseHandler && totalPages !== null)
					{
						this.UpdateElementsAndPagination(totalPages, null, result, null)
						return
					}
				}

				this.search.result = result
				this.Set<DataTableRef['states']>({ emptyText: this.constants.NoItemsMatchSearchCriteria.replace('%s', input) })
			}

			// Vaciar los Resultados de Busqueda al borrar el String de filtro.
			else
			{
				this.search.result = undefined
				this.Set<DataTableRef['states']>({ emptyText: this.constants.NoItemsToDisplay })
			}
		},

		async ExportDataAsJSON(data?: Array<any>) : Promise<void>
		{
			// Validación de la Data antes de la exportación.
			const _items: Array<any> = data || (await this.GetIterableItems())

			// Comprobar que se tienen registros para exportar.
			if (this.fields.length === 0 && _items.length === 0)
			{
				return this.ShowToast('Exportar Archivo .JSON', 'No existe Data para exportar como archivo .JSON', 'danger')
			}

			// Configuraciones según cada caso.
			const { exportFieldNamesToExclude } = this.states

			// Generar las Columnas.
			const columnKeys = this.fields
			.filter((x: any) => !(exportFieldNamesToExclude.find((c: string) => c === x.key)))
			.map((x: any) => ({ key: x.key, label: x.label }))

			// Generar el Array con la data para exportar.
			const items: Array<any> = []
			for (const item of _items)
			{
				const itemObject: any = {}
				for (const key of columnKeys)
				{
					const columnKey = key.key, columnLabel = key.label
					const exclude = exportFieldNamesToExclude.find((c: string) => c === columnKey)
					if (columnKey !== exclude)
					{
						const itemValue = item[columnKey]
						const value = Array.isArray(itemValue) && itemValue.length === 1 ? itemValue[0] : itemValue
						itemObject[columnLabel] = this.ParseValueForDataExports('JSON', columnKey, value)
					}
				}
				items.push(itemObject)
			}

			// Generar la Data codificada para ser guardada como archivo .JSON
			const text = JSON.stringify(items)
			const dataURI = `data:application/json;charset=utf-8,${ encodeURIComponent(text) }`
			const fileName = new Date().getTime().toString()

			// Crear un elemento temporal <a> para descargar el archivo.
			const aElement = document.createElement('a')
			aElement.setAttribute('href', dataURI)
			aElement.setAttribute('download', `${ fileName }.json`)
			aElement.click()
		},

		async ExportDataAsXLSX(asCSV: boolean, data?: Array<any>) : Promise<void>
		{
			// Validación de la Data antes de la exportación.
			const _items: Array<any> = data || (await this.GetIterableItems())
			const fileExtension = asCSV ? 'CSV' : 'XLSX'

			// Comprobar que se tienen registros para exportar.
			if (this.fields.length === 0 && _items.length === 0)
			{
				return this.ShowToast(`Exportar Archivo .${ fileExtension }`, `No existe Data para exportar como archivo .${ fileExtension }`, 'danger')
			}

			// Configuraciones según cada caso.
			const { exportFieldNamesToExclude, xlsxWorkSheetName } = this.states

			// Referencias.
			const excelBook  = new ExcelBook()
			const workBook   = excelBook.getWorkBook()
			const excelSheet = new ExcelSheet(workBook, xlsxWorkSheetName)
			const workSheet  = excelSheet.getWorkSheet()

			// Generar las Columnas.
			const columnKeys: Array<string> = []
			const columns: Array<any> = this.fields
			.filter((x: any) => !(exportFieldNamesToExclude.find((c: string) => c === x.key)))
			.map((x: any) => { columnKeys.push(x.key); return ({ key: x.key, header: x.label, width: 20 }) })

			// Definir las Columnas (Headers) del documento XLSX.
			excelSheet.setColumns(columns).setRowHeight(workSheet.getRow(1), 20)

			// Aplicar un estilo al Header.
			workSheet.getRow(1).eachCell((cell) => {
				excelSheet
					.setCellAlignment(cell, 'center', 'middle')
					.setCellFontBold(cell)
					.setCellFontColor(cell, 'FFFFFFFF')
					.setCellSolidFill(cell, '4C78B3')
				;
			})

			// Agregar la Información del resto de filas.
			let currentRowIndex = 2
			for (const item of _items)
			{
				let currentCellIndex = 1
				const row = workSheet.getRow(currentRowIndex++)
				excelSheet.setRowHeight(row, 20)

				for (const columnKey of columnKeys)
				{
					const cell = row.getCell(currentCellIndex++)
					const itemColumn = item[columnKey]
					const value = Array.isArray(itemColumn) && itemColumn.length === 1 ? itemColumn[0] : itemColumn
					cell.value = this.ParseValueForDataExports('XLSX', columnKey, value)
					excelSheet.setCellAlignment(cell, undefined, 'middle')
				}
			}

			// Crear el archivo XLSX (o CSV).
			const fileName = new Date().getTime().toString();
			(asCSV) ? await excelBook.saveFileAsCSV(fileName) : await excelBook.saveFileAsXLSX(fileName)
		},

		GetActionCellContentClass(type: string) : string
		{
			return `dt-action-cell-${ type } ${ this.IsStacked ? 'dt-stacked' : '' }`.trim()
		},

		GetActionCellsClass() : string
		{
			return `dt-action-cell ${ this.IsStacked ? 'dt-stacked' : '' }`.trim()
		},

		GetClass(name: string) : string
		{
			let cls = ''
			switch (name)
			{
				case 'dt-tbody':
				{
					if (this.IsStacked) return `${ name } dt-stacked`
					return name
				}
				case 'dt-tbody-tr':
				{
					cls += 'dt-tbody-tr '
					if (this.condensed) cls += 'dt-condensed '
					if (this.IsStacked) cls += 'dt-stacked '
					if (!this.selectable || this.IsEmpty()) cls += 'not-selectable '
					return cls.trim()
				}
				default:
				{
					return ''
				}
			}
		},

		GetCurrentRow() : any
		{
			return this.currentRow
		},

		GetDataTableClasses() : { ['dt-stacked']: boolean }
		{
			return { ...this.GetResponsiveClass, ['dt-stacked']: this.IsStacked }
		},

		GetDetailsProps(objectDetails: any) : Array<{ key: string, value: any }>
		{
			// Array que contiene la Definición para el apartado 'Details'.
			const details: Array<{ key: string, value: any }> = []

			// Limpiar las 'Propiedades' de cualquier posible 'Flag'.
			Object.keys(objectDetails).forEach((key) => {
				const keyFlags = key.split('|')
				const hasAnyFlag = keyFlags.length > 1
				const newKey = hasAnyFlag ? keyFlags.shift() : key
				details.push({ key: newKey!, value: objectDetails[key] })
			})

			return details
		},

		GetEmptyText() : string
		{
			return this.states.emptyText || ''
		},

		GetFetchText() : string
		{
			return this.constants.FetchingItemsFromServer
		},

		GetItems() : Function | Array<any>
		{
			if (this.search.input !== '' && this.search.result)
			{
				this.UpdateNumRows(this.search.result.length)
				return this.search.result
			}
			else
			{
				if (typeof this.itemsProvider === 'function') return this.itemsProvider
				this.UpdateNumRows(this.items.length)
				return this.items
			}
		},

		async GetIterableItems() : Promise<Array<any>>
		{
			if (typeof this.itemsProvider === 'function') return await this.itemsProvider()
			return this.items
		},

		GetMaxHeight() : string
		{
			const { maxHeight } = this.states
			if (maxHeight)
			{
				if (typeof maxHeight === 'number') return `max-height: ${ maxHeight }px;`
				else return `max-height: ${ maxHeight };`
			}
			return ''
		},

		GetSearchInputDisabled() : boolean
		{
			return this.states.searchInputDisabled
		},

		GetStackedClass() : string
		{
			return this.IsStacked ? 'dt-stacked' : ''
		},

		GetTableClasses() : string
		{
			const fontsize = this.$props.fontsize ? ` dt-fontsize-${ this.$props.fontsize }` : ''
			const overflowHiddenOnEmptyData = this.fields.length === 0 && this.items.length === 0 ? ' dt-table-empty' : ''
			return 'dt-table' + fontsize + overflowHiddenOnEmptyData
		},

		HandleExportDataType(format: string, data: any) : void
		{
			switch (format)
			{
				case 'CSV':
					this.ExportDataAsXLSX(true, data)
					break
				case 'JSON':
					this.ExportDataAsJSON(data)
					break
				case 'XLSX':
					this.ExportDataAsXLSX(false, data)
					break
			}
		},

		IsEmpty() : boolean
		{
			return this.fields.length === 0 && this.items.length === 0
		},

		IsValidCheckBox(field: any) : boolean
		{
			if (field.checkbox?.editable) return true
			return false
		},

		IsValidEntry(field: any) : boolean
		{
			if (field.entry?.editable) return true
			return false
		},

		OnCheckSearchInput(fieldKey: any) : void
		{
			const fromFieldsArray  = this.fields.find((x) => x.key === fieldKey)
			const fromStackedArray = fromFieldsArray || this.states.stackedDetailsItemFlags.find((x) => x.flagKey === fieldKey)
			this.Set<DataTableRef['states']>({ searchBy: fromFieldsArray?.searchKey || fromStackedArray?.flagSearchKey || fieldKey, searchInputDisabled: fieldKey === '' })
		},

		OnDTButtonClick(key: string, row: any, button: any) : void
		{
			PrintHelper.warning('[DataTable]:onDTButtonClick event triggered')
			this.$emit('onDTButtonClick', key, row, button)
		},

		OnDTExportDataClick(filterValue: AppValues.PeriodsChoiceList | AppValues.QuantityChoiceList) : void
		{
			PrintHelper.warning('[DataTable]:onDTExportDataClick event triggered')
			const { exportDataFilterType, exportFileFormatSelected } = this.states
			this.$emit('onDTExportDataClick', exportFileFormatSelected, exportDataFilterType, filterValue)
			this.Set<DataTableRef['states']>({ exportFileFormatSelected: undefined, showExportDataSelection: false })
		},

		OnDTExportToFileClick(exportType: string) : Promise<void> | undefined
		{
			PrintHelper.warning('[DataTable]:onDTExportToFileClick event triggered')

			// Decargar los Registros actuales mostrados en el componente 'DataTable' si 'exportDataAsAsync' es FALSE.
			const { exportDataAsAsync, exportFileFormatSelected } = this.states
			if (!exportDataAsAsync)
			{
				switch (exportType)
				{
					case 'CSV' : return this.ExportDataAsXLSX(true)
					case 'JSON': return this.ExportDataAsJSON()
					case 'XLSX': return this.ExportDataAsXLSX(false)
				}
			}

			// Comprobar si se debe ocultar el 'Popup' de Periodo.
			if (exportType === null || exportFileFormatSelected === exportType) this.Set<DataTableRef['states']>({ exportFileFormatSelected: undefined, showExportDataSelection: false })

			// De lo contario, simplementa actualizar al nuevo 'Formato'.
			else this.Set<DataTableRef['states']>({ exportFileFormatSelected: exportType, showExportDataSelection: true })
		},

		OnDTInputCheckBoxChanged(row: any) : void
		{
			PrintHelper.warning('[DataTable]:onDTInputCheckBoxChanged event triggered')
			this.$emit('onDTInputCheckBoxChanged', row)
		},

		OnDTInputEntryKeyUp(row: any) : void
		{
			PrintHelper.warning('[DataTable]:onDTInputEntryKeyUp event triggered')
			this.$emit('onDTInputEntryKeyUp', row)
		},

		OnDTNewButtonClick() : void
		{
			PrintHelper.warning('[DataTable]:onDTNewButtonClick event triggered')
			this.$emit('onDTNewButtonClick')
		},

		OnDTPaginationChanged(page: number) : void
		{
			// Evento de Paginación según uso de Campo Buscar.
			const { isSearchCurrentlyActive, preventDefaultOnPaginationChange } = this.states

			// Prevenir el proceso completo de cambio de paginación.
			if (!preventDefaultOnPaginationChange)
			{
				const eventName = isSearchCurrentlyActive ? 'onDTCurrentSearchPaginationChanged' : 'onDTPaginationChanged'

				// Limpiar Tabla para obtener posibles nuevos registros.
				if (!isSearchCurrentlyActive) this.ClearData()
				this.Set<DataTableRef['states']>({ emptyText: this.constants.RefreshingItems, isFetching: true })

				// Información del Evento axionado.
				this.$emit(eventName, page, { searchKey: this.states.searchBy, searchValue: this.search.input })
			}
		},

		OnDTRefreshButtonClick() : void
		{
			PrintHelper.warning('[DataTable]:onDTRefreshButtonClick event triggered')
			this.ClearData()
			this.Set<DataTableRef['states']>({ emptyText: this.constants.RefreshingItems, isFetching: true })
			this.$emit('onDTRefreshButtonClick', this.Pagination.currentPage || 1)
		},

		OnDTSearchButtonClicked() : void
		{
			PrintHelper.warning('[DataTable]:onDTSearchButtonClicked event triggered')
			if ((!this.states.isLocalSearch) && this.search.input !== '') this.Set<DataTableRef['states']>({ isSearchCurrentlyActive: true })
			this.$emit('onDTSearchButtonClicked', { searchKey: this.states.searchBy, searchValue: this.search.input })
		},

		OnResponsiveBreakpoint(breakpoint: number) : void
		{
			// Controlar la Visibilidad de los elementos según el Breakpoint.
			if (!this.states.preventDefaultStacked)
			{
				const stacked = breakpoint <= Breakpoints.Large
				this.Set<DataTableRef['states']>({ stacked })
				this.$emit('onDTStackedUpdated', stacked)
			}
		},

		OnRowClickHandler(clickType: string, item: any, index: number) : void
		{
			// Por prioridad, si el valor de la propiedad 'selectable' es FALSE, omitir la ejecución de este evento.
			if (!this.selectable) return

			PrintHelper.warning(`[DataTable]:onRow${ clickType } event triggered`)
			this.currentRow = { item, index }
			this.$emit(`onRow${ clickType }`, item, index)
		},

		OnRowsSelected(rows: Array<any>) : void
		{
			// Por prioridad, si el valor de la propiedad 'selectable' es FALSE, omitir la ejecución de este evento.
			if (!this.selectable) return

			PrintHelper.warning(`[DataTable]:onRowsSelected event triggered`)
			if (rows.length === 0)
			{
				this.ClearCurrentRow()
				this.$emit('onRowsUnselected')
			}
		},

		ParseCellContent(cell: any) : any
		{
			if (typeof cell !== 'object') return cell
			return cell.value
		},

		async ParseDetailsItemFlags() : Promise<void>
		{
			// Ejecutar método unicamente si 'stackedDetailsItemFlags' esta vacio.
			const { stacked, stackedDetailsItemFlags } = this.states
			if (!stacked || stackedDetailsItemFlags.length > 0) return

			// Array que contiene la Definición para el apartado 'Details'.
			const items = await this.GetIterableItems()
			const objectDetails = items[0]?._details

			// Analizar la 'construcción' de las 'keys' en busqueda de 'Flags'.
			Object.keys(objectDetails).forEach((key) => {
				// Objeto con todas las posibles 'Flags' de la 'Propiedad o Columna'.
				const flags = RecordHelper.parseFlags(key)
				if (flags.flagKey !== '') stackedDetailsItemFlags.push({ ...flags })
			})
		},

		ParseReadOnlyItemOptions(options: Array<string>)
		{
			return options.map((x) => ({ value: x, text: x, disabled: true })).sort((a, b) => a.text > b.text ? 1 : -1)
		},

		ParseTemplateCell(key: string) : string
		{
			return `cell(${ key })`
		},

		ParseTemplateHead(key: string) : string
		{
			return `head(${ key })`
		},

		ParseValueForDataExports(exportType: any, fieldKey: string, value: any) : any
		{
			switch (exportType) {
				case 'JSON':
					if ((!!value) && (value.constructor === Object)) return value?.text || value?.value
					if (value instanceof ActionItem) return value.getText()
					return value
				case 'XLSX':
					if (Array.isArray(value)) return JSON.stringify(value).replace('[', '').replace(']', '')
					if ((!!value) && (value.constructor === Object)) return value?.text || value?.value
					if (value instanceof ActionItem) return value.getText()
					return value
				default:
					return value
			}
		},

		ResetEmptyText() : void
		{
			this.Set<DataTableRef['states']>({ emptyText: this.constants.NoItemsToDisplay })
		},

		ResetFetchState() : void
		{
			this.Set<DataTableRef['states']>({ emptyText: this.constants.NoItemsToDisplay, isFetching: false })
		},

		ResolveActionButtonStyle(item: any) : string
		{
			const variant = item.variant || 'gray'
			const value = this.ColorKeyToColorVar(variant)
			const h = `var(--hsl-${ value }-h)`, s = `var(--hsl-${ value }-s)`, l = `var(--hsl-${ value }-l)`
			return `
				background-color: hsla(${ h }, ${ s }, ${ l }, .75);
				border-color: var(--${ value });
				border-width: 1px;
			`
		},

		SetActions(actions: string[]) : void
		{
			this.actions = actions
		},

		SetElements(fields: Array<any>, items: Array<any>, actions?: Array<string>) : void
		{
			if (fields) this.SetFields(fields)
			if (items) this.SetItems(items)
			if (actions) this.SetActions(actions)
			if (actions) this.BindFieldActionPermissions()
		},

		SetFetchingState(bool: boolean) : void
		{
			this.Set<DataTableRef['states']>({ isFetching: bool })
		},

		SetFetchingStateWithAlert(bool: boolean, title: string, message: string, variant = 'success') : void
		{
			this.Set<DataTableRef['states']>({ isFetching: bool })
			this.ShowToast(title, message, variant)
		},

		SetFields(fields: Array<any>) : void
		{
			this.fields = fields.filter((x) => x.key !== '_showDetails' && x.key !== '_details')
		},

		SetFieldVisible(fieldKey: string, visible: boolean) : void
		{
			if (!visible)
			{
				// Encontrar el Campo, almacenarlo temporalmente y removerlo del Array de Campos.
				const fieldToRemove = { index: <number | undefined> undefined, field: <any> undefined }

				for (let i = 0; i < this.fields.length; i++)
				{
					const field = this.fields[i]
					if (field.key === fieldKey)
					{
						fieldToRemove.index = i
						fieldToRemove.field = field
						break
					}
				}

				if (fieldToRemove.index !== undefined)
				{
					this.fields.splice(fieldToRemove.index, 1)
					this.fieldsRemoved.push(fieldToRemove.field)
				}
			}
		},

		SetItems(items: Array<any>) : void
		{
			this.items = items !== undefined ? items : []
			this.Set<DataTableRef['states']>({ emptyText: this.constants.NoItemsToDisplay, isFetching: false })
			this.UpdateNumRows(this.items.length)
		},

		SetItemsProvider(provider: (ctx: any, callback?: any) => Promise<Array<any>>) : void
		{
			this.itemsProvider = provider
			this.Set<DataTableRef['states']>({ emptyText: this.constants.NoItemsToDisplay, isFetching: false, isUsingCustomProvider: true })
		},

		SetMaxHeight(m: number | string) : void
		{
			this.Set<DataTableRef['states']>({ maxHeight: m })
		},

		SetPaginationPage(page: number, preventDefaults = false) : void
		{
			this.Pagination.currentPage = page
			if (!preventDefaults) this.OnDTPaginationChanged(page)
		},

		SetPermission(key: string, value: boolean) : void
		{
			(<any> this.permissions)[key] = value
		},

		SetSelectable(state: boolean) : void
		{
			this.selectable = state
		},

		ShowDetailsButton({ field, item }: any) : boolean
		{
			return this.states.stacked && (item._details && field?.showDetailsButton === true)
		},

		SortCompare(a: any, b: any, key: string, sortDesc: boolean, formatter: any, compareOptions: object, compareLocale: string) : number | undefined
		{
			// Celdas de las filas a comparar.
			const field = this.fields.find((x: any) => x.key === key) as BMInterfaces.FieldDefinition
			const rows: { [key: string]: any } = { a, b }
			const cell: any = { a: rows.a[key], b: rows.b[key] }

			// Solo realizar las comparaciones si las celdas son diferentes a 'undefined'.
			if (cell?.a && cell?.b)
			{
				// Comparación personalizado para la columna 'actions'.
				if (field?.isAction)
				{
					if ((cell.a.length === 1 && cell.b.length === 1))
					{
						if (cell.a[0]?.sort && cell.b[0]?.sort)
						{
							// Propiedades que serán evaluadas por la comparación.
							const values: any = { a: '', b: '' }

							// Evaluación de la propiedad 'sort' de ambas celdas.
							for (const cellKey of ['a', 'b'])
							{
								for (const sorting of cell[cellKey][0].sort)
								{
									switch (sorting.from) {
										case '$row':
											values[cellKey] += rows[cellKey][sorting.key]
											break
										case '$self':
											values[cellKey] += cell[cellKey][0][sorting.key]
											break
									}
								}
							}
							return this.SortCompareValues(values.a, values.b, compareLocale, compareOptions)
						}
					}
				}
				return this.SortCompareValues(cell.a, cell.b, compareLocale, compareOptions)
			}
		},

		SortCompareValues(a: (number | Date | string), b: (number | Date | string), compareLocale: any, compareOptions: any) : number
		{
			if ((typeof a === 'number' && typeof b === 'number') || (a instanceof Date && b instanceof Date))
			{
				return a < b ? -1 : a > b ? 1 : 0
			}
			else
			{
				const aStr = a.toString()
				const bStr = b.toString()

				// Comprobar que los valores que son strings, puedan ser convertidos a Date
				// para realizar una correcta comparación de las fechas.
				if (TypesHelper.isDateValidAsString(aStr) && TypesHelper.isDateValidAsString(bStr)) {

					// El constructor Date, espera una fecha con formato 'MM-dd-YYYY'.
					const aSplit = aStr.split(' ')
					const bSplit = bStr.split(' ')

					// Invertir los días con los meses antes de enviarlos al constructor.
					const aDateSplit = aSplit[0].split('-')
					let aFormatted = `${ aDateSplit[1] }-${ aDateSplit[0] }-${ aDateSplit[2] }`

					const bDateSplit = bSplit[0].split('-')
					let bFormatted = `${ bDateSplit[1] }-${ bDateSplit[0] }-${ bDateSplit[2] }`

					// Concatenar la hora si es que se incluia en el string.
					if (aSplit.length === 2 && bSplit.length === 2)
					{
						aFormatted += ` ${ aSplit[1] }`
						bFormatted += ` ${ bSplit[1] }`
					}

					// Crear las instancias de 'Date' con las fechas modificadas.
					const aIsDate = new Date(aFormatted)
					const bIsDate = new Date(bFormatted)

					// Son efectivamente valores validos de fechas?
					if (aIsDate.toString() !== 'Invalid Date' && bIsDate.toString() !== 'Invalid Date')
					{
						const newA = aIsDate.getTime()
						const newB = bIsDate.getTime()
						return newA < newB ? -1 : newA > newB ? 1 : 0
					}

					return aStr.localeCompare(bStr, compareLocale, compareOptions)
				}
				return aStr.localeCompare(bStr, compareLocale, compareOptions)
			}
		},

		SortOrder(fieldKey: string, direction = 'asc') : void
		{
			this.sortBy = fieldKey
			this.sortDesc = direction === 'desc'
			this.sortDirection = direction

			// Asignar las Propiedades para la funcionalidad del 'Buscar'.
			const fromFieldsArray  = this.fields.find((x) => x.key === fieldKey)
			const fromStackedArray = fromFieldsArray || this.states.stackedDetailsItemFlags.find((x) => x.flagKey === fieldKey)
			this.Set<DataTableRef['states']>({ filterBy: fieldKey, searchBy: fromFieldsArray?.searchKey || fromStackedArray?.flagSearchKey || fieldKey, searchInputDisabled: fieldKey === '' })
		},

		UpdateElementsAndPagination(totalPages: number, fields: Array<any> | null, items: Array<any>, actions: Array<string> | null = null) : void
		{
			this.UpdateTotalPages(totalPages || 1)
			if (fields) this.SetFields(fields)
			this.SetItemsProvider(async () => items)
			if (actions) this.SetActions(actions)
			if (actions) this.BindFieldActionPermissions()
		},

		UpdateNumRows(numRows: number) : void
		{
			this.totalRows = numRows
		},

		UpdateRow(item: any, key = '_id') : void
		{
			for (let i = 0; i < this.items.length; i++) {
				const _item = this.items[i]
				if (_item[key] === item[key]) {
					this.items.splice(i, 1, item)
					this.DoInputSearch(null, null, null)
					break
				}
			}
		},

		UpdateTotalPages(n: number) : void
		{
			this.Pagination.localNumberOfPages = n
		}
	},

	/*************************************************/
	/** @Watch                                      **/
	/*************************************************/
	watch:
	{
		['items'](newValue: Array<any>) : void
		{
			if (newValue?.length > 0) this.ParseDetailsItemFlags()
		},

		['itemsProvider'](newValue: any) : void
		{
			if (newValue !== undefined) this.ParseDetailsItemFlags()
		},

		['search.input']() : void
		{
			this.DoInputSearch(null, null, null)
		}
	}
})

/**************************************************************************************************/
/** @export Component @name DataTable                                                            **/
/**************************************************************************************************/
export default DataTable

/**************************************************************************************************/
/** @export Type @name DataTableRef                                                              **/
/**************************************************************************************************/
export type DataTableRef = InstanceType<typeof DataTable>